import React, { ReactElement, useEffect } from 'react';

import { useRecoilState } from 'recoil';

import { htmlStringToReactElement } from '@common/parser';
import { css } from '@emotion/react';

import useRouterHook from '@lib/hook/useRouterHook';
import { palette } from '@lib/styles/palette';
import pixelToRem from '@lib/styles/pixelToRem';

import Icon from '@components/common/Icon';
import * as icons from '@components/common/Icon/svg';
import MoreString from '@components/common/MoreString';

import { recentSearchPriceRecordState } from '@recoils/search/atom';

interface RecentSearchRecord {
  heading: string;
  titleIcon?: keyof typeof icons;
}

function RecentSearchRecord({
  heading,
  titleIcon,
}: RecentSearchRecord): ReactElement {
  const { push } = useRouterHook();
  const [searchRecord, setSearchRecord] = useRecoilState(
    recentSearchPriceRecordState,
  );
  const isAd = titleIcon === 'adBadge';
  const isEmptyRecord = searchRecord.length === 0;

  const moveToDetailPricePageOf = (keyword: string) => {
    push(`/search-price?searchWord=${keyword}`);
  };
  const deleteAllSearchRecord = () => {
    setSearchRecord([]);
    localStorage.setItem('searchRecord', '[]');
  };

  useEffect(() => {
    const storedSearchRecord = JSON.parse(
      localStorage.getItem('searchRecord') ?? '[]',
    );

    setSearchRecord(storedSearchRecord);
  }, [setSearchRecord]);

  return (
    <section css={containerStyle} tabIndex={0}>
      <h2 css={headingStyle}>
        <span className="title">{htmlStringToReactElement(heading)}</span>
        {titleIcon && <Icon className="titleIcon" icon={titleIcon} />}
        <button
          type="button"
          aria-label="removeAll"
          css={deleteButtonStyle}
          onClick={deleteAllSearchRecord}
        >
          모두 지우기
        </button>
      </h2>
      <div css={contentContainerStyle(isAd)} tabIndex={0}>
        <div css={contentStyle(isEmptyRecord)}>
          {isEmptyRecord && (
            <span className="nokeyword">최근 검색어가 없습니다</span>
          )}
          <div className="keywordContainer">
            {searchRecord.map(({ keyword, idx }) => {
              return (
                <button
                  type="button"
                  aria-label="searchKeyword"
                  onClick={() => moveToDetailPricePageOf(keyword)}
                  className={`keyword`}
                  key={idx}
                >
                  <MoreString
                    string={keyword}
                    stringLimit={8}
                    lineCount={1}
                    foldedState={true}
                  />
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

const containerStyle = css`
  width: 100%;
  height: auto;
  position: relative;

  @media (min-width: 1024px) {
    padding: ${pixelToRem(0, 40)};
  }
`;

const headingStyle = css`
  color: ${palette.black10};
  display: flex;
  justify-content: space-between;
  font-size: ${pixelToRem(20)};
  line-height: ${pixelToRem(26)};
  letter-spacing: ${pixelToRem(-0.5)};

  .titleIcon {
    vertical-align: middle;
    margin-bottom: 3px;
  }

  @media (max-width: 599px) {
    padding: ${pixelToRem(0, 20)};

    .title {
      width: 200px;
    }

    align-items: flex-start;
  }

  @media (min-width: 600px) and (max-width: 1023px) {
    padding: ${pixelToRem(0, 24)};
  }

  @media (min-width: 1024px) {
  }
`;

const contentContainerStyle = (isAd: boolean) => css`
  margin: ${pixelToRem(30, 0, 56, 0)};
  border-top: ${isAd ? 0 : `1px solid ${palette.gray6}`};
  padding-top: ${isAd ? 0 : `${pixelToRem(40)}`};
  /* min-height: ${pixelToRem(300)}; */
  height: auto;

  @media (max-width: 599px) {
    margin: ${pixelToRem(20, 0, 56, 0)};
    padding: ${pixelToRem(0, 0, 0, 20)};
    border: none;
  }

  @media (min-width: 600px) and (max-width: 1023px) {
    margin: ${pixelToRem(20, 0, 56, 0)};
    padding: ${pixelToRem(0, 0, 0, 24)};
    border: none;
  }
`;

const contentStyle = (isEmptyHistory: boolean) => css`
  font-size: ${pixelToRem(14)};
  max-width: ${isEmptyHistory ? '100%' : pixelToRem(662)};
  flex-wrap: wrap;
  display: flex;

  @media (max-width: 599px) {
    max-width: 100%;
  }

  @media (min-width: 600px) and (max-width: 1023px) {
    max-width: 100%;
  }

  *:not(:last-of-type) {
    margin-right: 16px;
  }

  .nokeyword {
    display: inline-block;
    width: 100%;
    text-align: center;
    margin-top: 49px;
    line-height: ${pixelToRem(18)};
    color: ${palette.gray5};
  }

  .keywordContainer {
    /* width: 100vw; */
    overflow: scroll;
    display: flex;
    flex-wrap: nowrap;
    -ms-overflow-style: none;
    scroll-snap-type: x mandatory;

    @media (min-width: 1024px) {
      width: 670px;
      flex-wrap: wrap;
    }

    ::-webkit-scrollbar {
      display: none;
      width: 0 !important;
    }
  }

  .keyword {
    display: inline-block;
    color: ${palette.gray6};
    background-color: ${palette.gray0};
    border-radius: ${pixelToRem(25)};
    padding: ${pixelToRem(12)};
    box-sizing: border-box;
    height: 43px;
    flex: 0 0 auto;
    scroll-snap-align: start;
    margin-right: 8px;

    @media (max-width: 599px) {
      padding: ${pixelToRem(10, 8)};
    }

    @media (min-width: 600px) and (max-width: 1023px) {
      padding: ${pixelToRem(10, 8)};
    }

    @media (min-width: 1024px) {
      margin-bottom: 16px;
    }
  }
`;

const deleteButtonStyle = css`
  font-size: ${pixelToRem(12)};
  line-height: 14px;
  color: ${palette.gray6};
  background-color: transparent;
  top: 6px;
  right: 0;
  align-self: center;
`;

export default RecentSearchRecord;
